import { create } from 'zustand';
import type { SessionBootstrap } from '@arcnet/contracts';

/**
 * Session state.
 *
 * `permissions` is a Set for O(1) `can()` -- it is consulted on nearly every
 * render, so a linear scan over ~75 strings per check adds up in a table with
 * per-row action buttons.
 *
 * This drives what the UI OFFERS, never what it ALLOWS. The server enforces
 * every one of these independently; a hidden button is a usability feature
 * (doc 07 section 1).
 */
interface SessionState {
  session: SessionBootstrap | null;
  permissions: Set<string>;
  activeTenantId: string | null;
  setSession: (s: SessionBootstrap | null) => void;
  setActiveTenant: (id: string | null) => void;
  can: (permission: string) => boolean;
  clear: () => void;
}

export const useSession = create<SessionState>((set, get) => ({
  session: null,
  permissions: new Set(),
  activeTenantId: null,

  setSession: (s) =>
    set({
      session: s,
      permissions: new Set(s?.permissions ?? []),
      activeTenantId:
        get().activeTenantId ?? s?.active_tenant_id ?? s?.scope.tenant_ids[0] ?? null,
    }),

  setActiveTenant: (id) => set({ activeTenantId: id }),
  can: (permission) => get().permissions.has(permission),
  clear: () => set({ session: null, permissions: new Set(), activeTenantId: null }),
}));

export const useCan = (): ((p: string) => boolean) => useSession((s) => s.can);

/**
 * Somebody who sees only their own seats (the TMS User role, docs/33 §4): the own permissions and
 * not the broad ones. Their portal is their extensions, their calls and their voicemail; the
 * account's management screens are not theirs (owner, 18 September 2026: "make sure the
 * permissions are all correct for the type of login").
 */
export const useOwnOnly = (): boolean => useSession((s) =>
  !s.permissions.has('extension:read') && s.permissions.has('extension:read:own'));

/**
 * Whether the workspace is this person's to open: one of their own seats takes calls in the
 * browser (0105). An agent's screen is offered to agents, and to nobody else -- a seat without
 * the feature reaches a dead end there (owner, 24 September 2026).
 */
export const useHasWorkspace = (): boolean =>
  useSession((s) => s.session?.workspace ?? false);
